import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PiggyBank, Sparkle, Lightning, Target, FloppyDisk, Copy, Trash, Printer, Info, TrendUp, Scales, ShareNetwork, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { exportRetirementPdf } from "@/lib/retirementPdf";

const COLORS = ["#6366f1", "#10b981", "#38bdf8", "#f59e0b", "#a78bfa", "#f43f5e"];
const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const DEFAULTS = {
  plan_id: "omers", country: "Canada", current_age: 40, retirement_age: 60, current_salary: 90000,
  salary_increase_pct: 2, years_service: 10, highest_avg_salary: "", buyback_years: 0, extra_service: 0,
  accrual_rate: 2, promotion: false, promotion_pct: 10, dc_balance: 0, contribution_pct: 5, employer_match_pct: 3,
  investment_growth: 5, rrsp_balance: 0, tfsa_balance: 0, other_investments: 0, extra_annual_savings: 0,
  additional_income: 0, cpp_start_age: 65, oas_start_age: 65, ss_start_age: 67, goal_annual_income: "",
};

const Disclaimer = () => (
  <div className="flex items-start gap-2 rounded-md bg-amber-500/10 text-amber-300/90 px-3 py-2 text-xs" data-testid="retirement-disclaimer">
    <Info className="h-4 w-4 shrink-0 mt-0.5" weight="bold" />
    These calculations are estimates for planning purposes only and are not official pension calculations. Final retirement benefits are determined by your pension administrator and applicable laws.
  </div>
);

function NumField({ label, k, inp, set, suffix, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative mt-1">
        <Input type="number" value={inp[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={testid || `rp-${k}`} className="bg-secondary/50 h-9" />
        {suffix && <span className="absolute right-3 top-1.5 text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export default function RetirementPlanner() {
  const qc = useQueryClient();
  const [inp, setInp] = useState(DEFAULTS);
  const [debounced, setDebounced] = useState(DEFAULTS);
  const reportRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const set = (k, v) => setInp((p) => ({ ...p, [k]: v }));

  const { data: meta } = useQuery({ queryKey: ["retirement-meta"], queryFn: async () => (await api.get("/retirement/meta")).data });

  // apply prefill once meta arrives
  useEffect(() => {
    if (meta?.prefill) {
      setInp((p) => ({ ...p, country: meta.prefill.country || p.country, current_salary: meta.prefill.current_salary || p.current_salary }));
    }
  }, [meta]);

  useEffect(() => { const t = setTimeout(() => setDebounced(inp), 350); return () => clearTimeout(t); }, [inp]);

  const access = meta?.access;
  const plans = meta?.plans || [];
  const plan = plans.find((p) => p.id === inp.plan_id) || {};
  const levers = plan.levers || [];
  const isDB = plan.type === "db";
  const isDC = plan.type === "dc";
  const isCanada = inp.country !== "United States";

  const { data: sim, isFetching: simFetching } = useQuery({
    queryKey: ["retirement-sim", debounced],
    queryFn: async () => (await api.post("/retirement/simulate", debounced)).data,
    enabled: !!access?.allowed,
    keepPreviousData: true,
  });

  const { data: scenarios = [] } = useQuery({ queryKey: ["retirement-scenarios"], queryFn: async () => (await api.get("/retirement/scenarios")).data, enabled: !!access?.allowed });

  const saveScenario = useMutation({
    mutationFn: async () => api.post("/retirement/scenarios", { name: `Scenario ${scenarios.length + 1}`, inputs: inp }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["retirement-scenarios"] }); toast.success("Scenario saved."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const scAction = useMutation({
    mutationFn: async ({ id, action, body }) => action === "delete" ? api.delete(`/retirement/scenarios/${id}`) : action === "dup" ? api.post(`/retirement/scenarios/${id}/duplicate`) : api.put(`/retirement/scenarios/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["retirement-scenarios"] }),
  });

  const share = useMutation({
    mutationFn: async () => (await api.post("/retirement/share", { name: plan.name ? `${plan.name} plan` : "Retirement plan", inputs: inp })).data,
    onSuccess: async (d) => {
      const url = `${window.location.origin}${d.path}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard.", { description: url }); }
      catch { toast.success("Share link ready", { description: url }); }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const doExportPdf = async () => {
    setPdfBusy(true);
    try {
      await exportRetirementPdf(reportRef.current, {
        title: plan.name ? `${plan.name} — Retirement Plan` : "Retirement Plan",
        subtitle: `Retire at ${inp.retirement_age} · ${inp.country}`,
      });
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error("Could not generate PDF. Try the Print option instead.");
    } finally { setPdfBusy(false); }
  };

  // comparison: simulate each saved scenario
  const { data: compare = [] } = useQuery({
    queryKey: ["retirement-compare", scenarios.map((s) => s.id + s.updated_at).join(",")],
    queryFn: async () => Promise.all(scenarios.map(async (s) => ({ ...s, result: (await api.post("/retirement/simulate", s.inputs)).data }))),
    enabled: !!access?.allowed && scenarios.length > 0,
  });

  const applyLever = (patch, msg) => { setInp((p) => ({ ...p, ...patch })); if (msg) toast.info(msg); };

  const quickLevers = useMemo(() => {
    const out = [
      { label: "Retire 1 yr later", patch: { retirement_age: Number(inp.retirement_age) + 1 } },
      { label: "Retire 5 yrs later", patch: { retirement_age: Number(inp.retirement_age) + 5 } },
      { label: "Retire 1 yr earlier", patch: { retirement_age: Math.max(Number(inp.current_age) + 1, Number(inp.retirement_age) - 1) } },
    ];
    if (isDB) {
      if (levers.includes("buyback_years")) out.push({ label: "Buy back 1 yr", patch: { buyback_years: Number(inp.buyback_years) + 1 } });
      if (levers.includes("promotion")) out.push({ label: "Add promotion", patch: { promotion: true } });
      out.push({ label: "+1 yr service", patch: { extra_service: Number(inp.extra_service) + 1 } });
    }
    if (isDC) {
      out.push({ label: "+2% contribution", patch: { contribution_pct: Number(inp.contribution_pct) + 2 } });
      out.push({ label: "+1% growth", patch: { investment_growth: Number(inp.investment_growth) + 1 } });
    }
    out.push({ label: "+$5k/yr savings", patch: { extra_annual_savings: Number(inp.extra_annual_savings) + 5000 } });
    return out;
  }, [inp, isDB, isDC, levers]);

  if (access && !access.allowed) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid="retirement-locked">
        <PiggyBank className="h-12 w-12 text-primary mx-auto mb-4" weight="duotone" />
        <h1 className="text-2xl font-black">Retirement Planner</h1>
        <p className="text-muted-foreground mt-2">This feature is currently available to Pro subscribers. Upgrade to model your retirement scenarios.</p>
        <Button className="mt-5 rounded-md font-semibold" onClick={() => window.location.assign("/settings/billing")} data-testid="rp-upgrade">Upgrade to Pro</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retirement-planner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-primary font-bold flex items-center gap-1.5"><PiggyBank className="h-4 w-4" weight="bold" /> Retirement</p>
          <h1 className="text-3xl font-black tracking-tight">Retirement Planner & Pension Simulator</h1>
          <p className="text-muted-foreground text-sm mt-1">Model "what-if" scenarios and see your estimated retirement income instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-md" onClick={doExportPdf} disabled={pdfBusy || simFetching} data-testid="rp-export-pdf"><FilePdf className="h-4 w-4 mr-1.5" weight="bold" /> {pdfBusy ? "Generating…" : "Download PDF"}</Button>
          <Button variant="outline" className="rounded-md" onClick={() => share.mutate()} disabled={share.isPending} data-testid="rp-share"><ShareNetwork className="h-4 w-4 mr-1.5" weight="bold" /> Share</Button>
          <Button variant="outline" className="rounded-md" onClick={() => window.print()} data-testid="rp-print"><Printer className="h-4 w-4 mr-1.5" weight="bold" /> Print</Button>
        </div>
      </div>
      <Disclaimer />

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Inputs */}
        <Card className="p-5 bg-card border-border space-y-4 h-fit" data-testid="rp-inputs">
          <div>
            <Label className="text-xs">Pension plan</Label>
            <Select value={inp.plan_id} onValueChange={(v) => set("plan_id", v)}>
              <SelectTrigger className="mt-1 bg-secondary/50 h-9" data-testid="rp-plan"><SelectValue /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.country !== "Any" ? ` · ${p.country}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Country</Label>
            <Select value={inp.country} onValueChange={(v) => set("country", v)}>
              <SelectTrigger className="mt-1 bg-secondary/50 h-9" data-testid="rp-country"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{(meta?.countries || ["Canada", "United States"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Current age" k="current_age" inp={inp} set={set} />
            <NumField label="Retirement age" k="retirement_age" inp={inp} set={set} />
            <NumField label="Annual salary" k="current_salary" inp={inp} set={set} suffix="$" />
            <NumField label="Salary growth" k="salary_increase_pct" inp={inp} set={set} suffix="%" />
          </div>

          {isDB && (
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
              <NumField label="Years of service" k="years_service" inp={inp} set={set} />
              <NumField label="Best avg salary (opt)" k="highest_avg_salary" inp={inp} set={set} suffix="$" />
              {levers.includes("buyback_years") && <NumField label="Buy-back years" k="buyback_years" inp={inp} set={set} />}
              {levers.includes("extra_service") && <NumField label="Extra service" k="extra_service" inp={inp} set={set} />}
              {levers.includes("accrual_rate") && <NumField label="Accrual rate" k="accrual_rate" inp={inp} set={set} suffix="%" />}
              {levers.includes("promotion") && (
                <div className="col-span-2 flex items-center justify-between">
                  <Label className="text-xs">Expect a promotion</Label>
                  <Switch checked={!!inp.promotion} onCheckedChange={(v) => set("promotion", v)} data-testid="rp-promotion" />
                </div>
              )}
            </div>
          )}

          {isDC && (
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
              <NumField label="Current balance" k="dc_balance" inp={inp} set={set} suffix="$" />
              <NumField label="Contribution" k="contribution_pct" inp={inp} set={set} suffix="%" />
              <NumField label="Employer match" k="employer_match_pct" inp={inp} set={set} suffix="%" />
              <NumField label="Investment growth" k="investment_growth" inp={inp} set={set} suffix="%" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
            <NumField label="RRSP / 401(k)" k="rrsp_balance" inp={inp} set={set} suffix="$" />
            <NumField label="TFSA / Roth" k="tfsa_balance" inp={inp} set={set} suffix="$" />
            <NumField label="Other investments" k="other_investments" inp={inp} set={set} suffix="$" />
            <NumField label="Extra savings/yr" k="extra_annual_savings" inp={inp} set={set} suffix="$" />
            <NumField label="Other income/yr" k="additional_income" inp={inp} set={set} suffix="$" />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
            {isCanada ? (
              <>
                <NumField label="CPP start age" k="cpp_start_age" inp={inp} set={set} />
                <NumField label="OAS start age" k="oas_start_age" inp={inp} set={set} />
              </>
            ) : (
              <NumField label="Social Security age" k="ss_start_age" inp={inp} set={set} />
            )}
          </div>

          <Button onClick={() => saveScenario.mutate()} className="w-full rounded-md font-semibold" data-testid="rp-save-scenario"><FloppyDisk className="h-4 w-4 mr-1.5" weight="bold" /> Save this scenario</Button>
        </Card>

        {/* Dashboard */}
        <div className="space-y-5" ref={reportRef}>
          <div className="grid sm:grid-cols-4 gap-3">
            <Card className="p-4 bg-card border-border" data-testid="rp-monthly"><p className="text-xs text-muted-foreground">Est. monthly income</p><p className="text-2xl font-black text-emerald-400">{usd(sim?.monthly_total)}</p></Card>
            <Card className="p-4 bg-card border-border" data-testid="rp-annual"><p className="text-xs text-muted-foreground">Est. annual income</p><p className="text-2xl font-black">{usd(sim?.annual_total)}</p></Card>
            <Card className="p-4 bg-card border-border" data-testid="rp-replacement"><p className="text-xs text-muted-foreground">Income replacement</p><p className="text-2xl font-black">{sim?.replacement_pct ?? 0}%</p></Card>
            <Card className="p-4 bg-card border-border" data-testid="rp-readiness"><p className="text-xs text-muted-foreground">Readiness score</p><p className={`text-2xl font-black ${(sim?.readiness_score || 0) >= 70 ? "text-emerald-400" : "text-amber-300"}`}>{sim?.readiness_score ?? 0}</p></Card>
          </div>

          {/* Quick what-if levers */}
          <Card className="p-4 bg-card border-border" data-testid="rp-levers">
            <p className="text-sm font-bold mb-2 flex items-center gap-1.5"><Lightning className="h-4 w-4 text-primary" weight="fill" /> What-if — tap to model instantly</p>
            <div className="flex flex-wrap gap-2">
              {quickLevers.map((l) => (
                <button key={l.label} onClick={() => applyLever(l.patch)} data-testid={`rp-lever-${l.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary transition-colors">{l.label}</button>
              ))}
              <button onClick={() => setInp(DEFAULTS)} className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground">Reset</button>
            </div>
          </Card>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4 bg-card border-border" data-testid="rp-chart-sources">
              <p className="text-sm font-bold mb-2">Income by source</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={sim?.sources || []} dataKey="annual" nameKey="label" cx="50%" cy="50%" outerRadius={80} label={(e) => (e.label || e.name || "").split(" ")[0]}>
                    {(sim?.sources || []).map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "#1e1e28", border: "1px solid #333", borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-4 bg-card border-border" data-testid="rp-chart-ages">
              <p className="text-sm font-bold mb-2">Monthly income by retirement age</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sim?.charts?.retire_age_comparison || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a35" />
                  <XAxis dataKey="age" stroke="#888" fontSize={11} /><YAxis stroke="#888" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "#1e1e28", border: "1px solid #333", borderRadius: 8 }} />
                  <Bar dataKey="monthly" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-4 bg-card border-border md:col-span-2" data-testid="rp-chart-growth">
              <p className="text-sm font-bold mb-2">Savings growth over your career</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={sim?.charts?.savings_growth || []}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a35" />
                  <XAxis dataKey="age" stroke="#888" fontSize={11} /><YAxis stroke="#888" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "#1e1e28", border: "1px solid #333", borderRadius: 8 }} />
                  <Area type="monotone" dataKey="balance" stroke="#10b981" fill="url(#g)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Insights */}
          {sim?.insights?.length > 0 && (
            <Card className="p-4 bg-card border-border" data-testid="rp-insights">
              <p className="text-sm font-bold mb-2 flex items-center gap-1.5"><Sparkle className="h-4 w-4 text-amber-300" weight="fill" /> Smart insights</p>
              <ul className="space-y-1.5">{sim.insights.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><TrendUp className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" weight="bold" /> {t}</li>)}</ul>
            </Card>
          )}

          {/* Goal planner */}
          <Card className="p-4 bg-card border-border" data-testid="rp-goal">
            <p className="text-sm font-bold mb-2 flex items-center gap-1.5"><Target className="h-4 w-4 text-primary" weight="fill" /> Goal planner</p>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-[220px]"><Label className="text-xs">Desired annual income</Label><Input type="number" value={inp.goal_annual_income} onChange={(e) => set("goal_annual_income", e.target.value)} placeholder="e.g. 90000" className="bg-secondary/50 h-9 mt-1" data-testid="rp-goal-input" /></div>
            </div>
            {sim?.goal && (
              <div className="mt-3 grid sm:grid-cols-4 gap-3 text-sm" data-testid="rp-goal-result">
                <div><p className="text-xs text-muted-foreground">Status</p><p className={`font-bold ${sim.goal.achievable ? "text-emerald-400" : "text-amber-300"}`}>{sim.goal.achievable ? "On track 🎉" : "Shortfall"}</p></div>
                <div><p className="text-xs text-muted-foreground">Annual shortfall</p><p className="font-bold">{usd(sim.goal.shortfall_annual)}</p></div>
                <div><p className="text-xs text-muted-foreground">Extra savings/yr needed</p><p className="font-bold">{usd(sim.goal.suggested_extra_annual_savings)}</p></div>
                <div><p className="text-xs text-muted-foreground">Age to hit goal</p><p className="font-bold">{sim.goal.required_retirement_age || "—"}</p></div>
              </div>
            )}
          </Card>

          {/* Scenario comparison */}
          {compare.length > 0 && (
            <Card className="p-4 bg-card border-border" data-testid="rp-compare">
              <p className="text-sm font-bold mb-3 flex items-center gap-1.5"><Scales className="h-4 w-4 text-primary" weight="fill" /> Compare scenarios</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-muted-foreground text-left"><th className="py-1.5">Scenario</th><th>Plan</th><th>Retire age</th><th>Monthly</th><th>Annual</th><th>Replace %</th><th></th></tr></thead>
                  <tbody>
                    {compare.map((c) => (
                      <tr key={c.id} className="border-t border-border" data-testid={`rp-compare-row-${c.id}`}>
                        <td className="py-2">
                          <input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && scAction.mutate({ id: c.id, action: "rename", body: { name: e.target.value } })}
                            className="bg-transparent border-b border-transparent hover:border-border focus:border-primary outline-none font-medium w-32" data-testid={`rp-rename-${c.id}`} />
                        </td>
                        <td className="text-muted-foreground">{c.result?.plan?.name}</td>
                        <td>{c.inputs?.retirement_age}</td>
                        <td className="font-bold text-emerald-400">{usd(c.result?.monthly_total)}</td>
                        <td>{usd(c.result?.annual_total)}</td>
                        <td>{c.result?.replacement_pct}%</td>
                        <td className="text-right whitespace-nowrap">
                          <button onClick={() => setInp({ ...DEFAULTS, ...c.inputs })} className="text-xs text-primary hover:underline mr-2" data-testid={`rp-load-${c.id}`}>Load</button>
                          <button onClick={() => scAction.mutate({ id: c.id, action: "dup" })} className="text-muted-foreground hover:text-foreground mr-2" data-testid={`rp-dup-${c.id}`}><Copy className="h-4 w-4 inline" /></button>
                          <button onClick={() => scAction.mutate({ id: c.id, action: "delete" })} className="text-muted-foreground hover:text-rose-400" data-testid={`rp-del-${c.id}`}><Trash className="h-4 w-4 inline" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Disclaimer />
        </div>
      </div>
    </div>
  );
}
